import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, QueryList, ViewChild, ViewChildren, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SelectEvent } from '@progress/kendo-angular-layout';
import { ITreeOptions } from 'angular-tree-component';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { LayerApi } from '../../../api/layer-api';
import { UserApi } from '../../../api/user-api';
import { IMCMapView } from '../../../classes/mcMapView';
import { environment } from '../../../environment';
import { InputConverter } from '../../../inputConverter';
import { IAuthenticationService } from '../../../services/interfaces/IAuthenticationService';
import { ILoggingService } from '../../../services/interfaces/ILoggingService';
import { LayerService } from '../../../services/layer.service';
import { LayerCatalogService } from '../../../services/layerCatalog.service';
import { PetlService } from '../../../services/petl.service';
import { ILayer, ILayerCatalogItem, IUserFolder, LogLevel } from '../../../types';
import { BaseComponent } from '../../base.component';
import { ExternalLayerCatalogComponent } from '../external-layer-catalog/external-layer-catalog.component';

enum ShareType {
    NotShared = 0,
    SharedBy = 1,
    SharedWith = 2
}

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-layer-catalog',
    styleUrls: [
        '../../sharedStyles/base.scss',
        '../../sharedStyles/telerik.scss',
        '../../sharedStyles/angular-tree-component.css',
        './layer-catalog.component.scss'
    ],
    templateUrl: './layer-catalog.component.html'
})
export class LayerCatalogComponent extends BaseComponent implements OnInit, AfterViewInit {
    @Input() applicationId: number = environment.appId;
    @Input() externalLayerTabTitle = 'External Layers';
    @Input() @InputConverter() mergeLayers: boolean = false;
    @Input() mapViewRef: IMCMapView;
    @Input() selectedLayers: ILayer[] = [];
    @Input() @InputConverter() filterLayersBeforeAdd: boolean = false;
    @Input() @InputConverter() external: boolean = true;
    @Input() @InputConverter() showMyLayers: boolean = true;
    @Input() @InputConverter() showMyDrawingLayers: boolean = true;

    private _userId = null;
    @Input()
    get userId() { return this._userId; }
    set userId(value: number) {
        this._userId = value;
        this.cdr.detectChanges();
    }
    userSubscriber: any;

    @Output() readonly layersAdded: EventEmitter<boolean> = new EventEmitter();
    @Output() readonly openShareLayers: EventEmitter<number> = new EventEmitter();
    @Output() readonly openUnshareLayers: EventEmitter<number> = new EventEmitter();

    @ViewChild('addExternalLayer') addExternalLayer: any; //: AddExternalLayerComponent; // todo TFS#13854

    externalLayerCatalogRef: ExternalLayerCatalogComponent;
    @ViewChildren('externalLayerCatalog') externalLayerCatalog: QueryList<ExternalLayerCatalogComponent>;

    currentTabIndex: number = 0;
    debounceTime = 500;
    filteredSystemLayerCatalogItems: ILayerCatalogItem[] = [];
    filteredUserLayerCatalogItems: ILayerCatalogItem[] = [];
    filteredUserDrawingLayerCatalogItems: ILayerCatalogItem[] = [];
    systemLayerCatalogItems: ILayerCatalogItem[] = null;
    userLayerCatalogItems: ILayerCatalogItem[] = null;
    userDrawingLayerCatalogItems: ILayerCatalogItem[] = null;
    searchInputForm: FormGroup;
    searchInputControl: FormControl = new FormControl();
    searchText: string;
    externalShowCatalog = true;

    userLayerFolders: any[] = [];
    drawLayerFolders: any[] = [];
    currentFolderId: number;
    parentFolderId: number;
    currentLayerId: number;
    showFolderEditor: boolean;
    showFolderPicker: boolean;
    folderEditing = false;
    newFolderName: FormControl;
    parentFolders: IUserFolder[] = [];
    loading: { status: boolean; text: string };
    systemLayerErrorMessage = null;

    userLayerNodes: any[];
    drawLayerNodes: any[];
    treeOptions: ITreeOptions;
    @ViewChild('userLayerTree') userMapTree;
    @ViewChild('drawLayerTree') drawMapTree;

    @Input()
    @InputConverter()
    public get active(): boolean { return this._active; }
    public set active(active: boolean) {
        this._active = active;
        if (active) {
            this.activate();
        } else {
            this.deactivate();
        }
    }
    private _active: boolean = false;

    /* tslint:disable:template-cyclomatic-complexity */
    constructor(_vcr: ViewContainerRef,
        @Inject('authenticationService') private readonly authService: IAuthenticationService,
        private readonly _formBuilder: FormBuilder,
        private readonly layerCatalogService: LayerCatalogService,
        private readonly layerService: LayerService,
        private readonly cdr: ChangeDetectorRef,
        @Inject('LoggingService') public LoggingService: ILoggingService,
        private readonly userApi: UserApi,
        private readonly layerApi: LayerApi,
        private readonly petlService: PetlService
    ) {
        super(_vcr);
        this.searchInputForm = this._formBuilder.group({
            searchInput: this.searchInputControl
        });
    }

    ngOnInit() {
        this.searchInputControl
            .valueChanges
            .pipe(
                debounceTime(this.debounceTime),
                distinctUntilChanged()
            ).subscribe(() => {
                if (this.currentTabIndex === 0) {
                    this.loadFilteredSystemLayerCatalogItems();
                    this.cdr.detectChanges();
                } else {
                    this.loadFilteredUserLayerCatalogItems();
                }
            });
        this.userLayerNodes = [];
        this.drawLayerNodes = [];
        this.treeOptions = {};
        this.loading = { status: false, text: 'Loading...' };
        this.newFolderName = new FormControl('', [Validators.required, this.nameInUse.bind(this)]); //tslint:disable-line
    }

    async ngAfterViewInit() {
        this.mapViewRef = await this.setupMapViewRef(this.mapViewRef);
        this.isReady();
    }

    async addSelectedLayers() {
        this.refreshSelectedLayers();
        const userLayerType = 3;
        if (this.filterLayersBeforeAdd && !this.selectedLayers.some(x => x.typeId === userLayerType)) {
            const petlLayers = this.selectedLayers.filter(sl => sl.isPETL);
            if (petlLayers.length) {
                this.layerService.addMapcoreLayersToMap(petlLayers); // May want to use the PETL service to load these if we want it to overwrite currently loaded PETL layers
            }
            const layers = this.selectedLayers.filter(sl => !sl.isPETL);
            if (layers.length) {
                const addLayers = new CustomEvent('addingMapcoreLayersToMap', { detail: layers, composed: true });
                window.dispatchEvent(addLayers);
            }
        } else {
            const layerSettings = this.selectedLayers.map(sl => {
                if (sl.isPETL) {
                    return sl;
                }
                return { id: sl.id };
            });
            this.layerService.addMapcoreLayersToMap(layerSettings);
        }
        this.layersAdded.emit(true);
    }

    async deleteSelectedLayers() {
        this.refreshSelectedLayers();
        await Promise.all(this.selectedLayers.map(l => this.deleteLayer(l, false, true)));
        this.userLayerCatalogItems = [];
        this.userDrawingLayerCatalogItems = [];
        this.selectedLayers = [];
        await this.refreshUserLayerCatalog();
    }

    clearFilterAndSelected() {
        this.selectedLayers = [];
        this.searchText = '';
    }

    filterCatalogItems(layerCatalogItems: ILayerCatalogItem[]) {
        const copyOfArray = layerCatalogItems.concat([]);
        const newArray: ILayerCatalogItem[] = [];
        for (const layerCatalogItem of copyOfArray) {
            let currentItem: ILayerCatalogItem = null;
            if (layerCatalogItem.layer) {
                currentItem = layerCatalogItem;
            } else {
                currentItem = { ...layerCatalogItem };
            }
            if ((currentItem.layer && currentItem.layer.id.toString() === this.searchText)
                || (currentItem.isSelected || !this.searchText || this.isMatchToSearchText(currentItem.name, currentItem.layer ? currentItem.layer.description : null))) {
                newArray.push(currentItem);
            }
            if (currentItem.layerCatalogItems) {
                if (newArray.indexOf(currentItem) < 0) {
                    currentItem.layerCatalogItems = this.filterCatalogItems(currentItem.layerCatalogItems);
                    if (this.searchText) {
                        currentItem.expanded = currentItem.layerCatalogItems.length > 0;
                    }
                }
            }
            if (newArray.indexOf(currentItem) < 0 && currentItem.layerCatalogItems && currentItem.layerCatalogItems.length > 0) {
                newArray.push(currentItem);
            }
        }
        return newArray;
    }

    getSelectedLayers(layerCatalogItems: ILayerCatalogItem[]) {
        const layers: ILayer[] = [];
        for (const layerCatalogItem of layerCatalogItems) {
            if (layerCatalogItem.layer && layerCatalogItem.isSelected) {
                if (layerCatalogItem.ispetl) {
                    layers.push(this.petlService.getProjectLayerSettings({ id: layerCatalogItem.id, description: layerCatalogItem.name }));
                } else {
                    layers.push(layerCatalogItem.layer);
                }
            } else if (layerCatalogItem.layerCatalogItems) {
                layers.push(...this.getSelectedLayers(layerCatalogItem.layerCatalogItems));
            }
        }
        return layers;
    }

    itemSelectedChanged(layerCatalogItems: ILayerCatalogItem) {
        if (layerCatalogItems && layerCatalogItems.name && layerCatalogItems.isSelected !== undefined) {

            if (this.userLayerCatalogItems && this.currentTabIndex === 1) {
                const selectedLayerIndex = this.userLayerCatalogItems.findIndex(x => x.layer.id === layerCatalogItems.layer.id);
                if (selectedLayerIndex !== null) {
                    this.userLayerCatalogItems[selectedLayerIndex].isSelected = layerCatalogItems.isSelected;
                }
            } else if (this.userDrawingLayerCatalogItems && this.currentTabIndex === 2) {
                const selectedLayerIndex = this.userDrawingLayerCatalogItems.findIndex(x => x.layer.id === layerCatalogItems.layer.id);
                if (selectedLayerIndex !== null) {
                    this.userDrawingLayerCatalogItems[selectedLayerIndex].isSelected = layerCatalogItems.isSelected;
                }
            }

        }
        this.refreshSelectedLayers();
    }

    isMatchToSearchText(name: string, description?: string) {
        const nameLower = name.toLowerCase();
        const searchTextLower = this.searchText.toLowerCase();
        const searchTermArray = searchTextLower.split(' ');
        let allTermsFoundName = true;
        for (const term of searchTermArray) {
            allTermsFoundName = allTermsFoundName && nameLower.indexOf(term) >= 0;
        }
        let allTermsFoundDesc = false;
        if (description) {
            const descLower = description.toLowerCase();
            allTermsFoundDesc = true;
            for (const term of searchTermArray) {
                allTermsFoundDesc = allTermsFoundDesc && descLower.indexOf(term) >= 0;
            }
        }
        return allTermsFoundName || allTermsFoundDesc;
    }

    getFilteredCatalogItems(fullList: ILayerCatalogItem[]) {
        if (fullList) {
            const filteredItems = this.filterCatalogItems(fullList);
            const sortLevel = (level: ILayerCatalogItem[]) => {
                for (const catalogItem of level) {
                    if (catalogItem.layerCatalogItems) {
                        catalogItem.layerCatalogItems = sortLevel(catalogItem.layerCatalogItems);
                    }
                }
                return level.sort(this.layerCatalogItemsSort);
            };
            return sortLevel(filteredItems);
        } else {
            return [];
        }
    }

    layerCatalogItemsSort = (a: ILayerCatalogItem, b: ILayerCatalogItem) => {
        let retVal = 0;
        if ((a.displayOrder !== null && a.displayOrder !== undefined) || (b.displayOrder !== null && b.displayOrder !== undefined)) {
            const aOrder = a.displayOrder || 99999;
            const bOrder = b.displayOrder || 99999;
            if (aOrder > bOrder) {
                retVal = 1;
            } else if (aOrder < bOrder) {
                retVal = -1;
            }
        } else if (a.name.toLowerCase() > b.name.toLowerCase()) {
            retVal = 1;
        } else if (a.name.toLowerCase() < b.name.toLowerCase()) {
            retVal = -1;
        }
        return retVal;
    }

    loadFilteredSystemLayerCatalogItems() {
        this.filteredSystemLayerCatalogItems = this.getFilteredCatalogItems(this.systemLayerCatalogItems);
    }

    async loadFilteredUserLayerCatalogItems() {
        this.filteredUserLayerCatalogItems = this.getFilteredCatalogItems(this.userLayerCatalogItems);
        this.filteredUserDrawingLayerCatalogItems = this.getFilteredCatalogItems(this.userDrawingLayerCatalogItems);

        if (this.currentTabIndex === 1) {
            await this.setTree(this.userLayerFolders, this.filteredUserLayerCatalogItems);
        } else if (this.currentTabIndex === 2) {
            await this.setTree(this.drawLayerFolders, this.filteredUserDrawingLayerCatalogItems);
        }

        this.treeOptions = {};
        this.cdr.detectChanges();
    }

    refreshSelectedLayers() {
        let systemAndUserItems = [];
        if (this.systemLayerCatalogItems) {
            systemAndUserItems = systemAndUserItems.concat(this.systemLayerCatalogItems);
        }
        if (this.userLayerCatalogItems) {
            systemAndUserItems = systemAndUserItems.concat(this.userLayerCatalogItems);
        }
        if (this.userDrawingLayerCatalogItems) {
            systemAndUserItems = systemAndUserItems.concat(this.userDrawingLayerCatalogItems);
        }
        this.selectedLayers = [];
        this.selectedLayers = this.getSelectedLayers(systemAndUserItems);
        this.cdr.detectChanges();
    }

    deactivate() {
        if (this.externalLayerCatalogRef) {
            this.externalLayerCatalogRef.active = false;
        }
        this.clearFilterAndSelected();
        this.filteredSystemLayerCatalogItems = [];
        this.filteredUserLayerCatalogItems = [];
        this.filteredUserDrawingLayerCatalogItems = [];
        this.externalShowCatalog = true;
        this.currentTabIndex = 0;
    }

    async activate() {
        this.loading = { status: true, text: 'Loading...' };
        if (!this.userSubscriber) {
            this.userSubscriber = await this.authService.GetCurrentUser().subscribe(user => {
                if (user && user.id) {
                    this.userId = user.id;
                } else {
                    this.userId = null;
                }
            });
        }
        this.systemLayerCatalogItems = [];
        this.userLayerCatalogItems = [];
        this.userDrawingLayerCatalogItems = [];
        this.selectedLayers = [];
        await this.layerCatalogService.generateCatalog(environment.appId).then(data => {
            this.systemLayerCatalogItems = data;
            this.loadFilteredSystemLayerCatalogItems();
        });
        if (this.userId) {
            if (this.currentTabIndex !== 0) {
                await this.updateLayerFolders();
                await this.refreshUserLayerCatalog();
            }
        }
        this.loading.status = false;
        this.cdr.detectChanges();
    }

    async refreshUserLayerCatalog() {
        if (this.userId) {
            this.userLayerCatalogItems = [];
            this.userDrawingLayerCatalogItems = [];
            const items = await this.layerCatalogService.getUserLayerCatalog(this.userId);
            for (const item of items) {
                if ((item.layer.sources && item.layer.sources.length > 0) && item.layer.sources.filter(x => x.sourceType === 'DRAWING_DATA').length > 0) {
                    item.id = item.layer.id;
                    this.userDrawingLayerCatalogItems.push(item);
                } else {
                    this.userLayerCatalogItems.push(item);
                }
            }
            await this.loadFilteredUserLayerCatalogItems();
        }
    }

    async onTabSelect(e: SelectEvent) {
        //refresh folders and layers
        this.currentTabIndex = e.index;

        if (this.currentTabIndex !== 0) {
            this.loading = { status: true, text: 'Loading...' };
            if (e.title !== this.externalLayerTabTitle) {
                await this.updateLayerFolders();
                await this.refreshUserLayerCatalog();
            }
        }

        if (this.external) {
            const sub = this.externalLayerCatalog.changes.subscribe((ql: QueryList<ExternalLayerCatalogComponent>) => {
                sub.unsubscribe();
                this.externalLayerCatalogRef = ql.first;

                if (this.externalLayerCatalogRef) {
                    this.externalLayerCatalogRef.active = e.title === this.externalLayerTabTitle;
                }
            });
        }

        this.loading.status = false;
        this.cdr.detectChanges();
    }

    async addFolder(folder) {
        this.currentFolderId = null;
        this.parentFolderId = folder ? folder.id : undefined;
        const layerType = this.currentTabIndex === 1 ? 'layers' : 'drawing-layers';
        this.parentFolders = await this.userApi.queryUserFolders(this.userId, this.applicationId, layerType);
        this.parentFolders.push({ name: 'No parent', id: 0 });

        this.showFolderEditor = true;
        this.newFolderName.setValue('');
        this.folderEditing = false;
        this.cdr.detectChanges();
    }

    nameInUse(control: AbstractControl) {
        const parentFolder = this.parentFolders.find(f => f.id === this.currentFolderId);
        if (this.currentFolderId && (!parentFolder || control.value === parentFolder.name)) {
            return null;
        }

        return this.parentFolders
            .map(f => f.name)
            .includes(control.value) ? { nameInUse: { value: control.value } } : null;
    }

    async editFolder(folder) {
        this.currentFolderId = folder.id;
        const parentFolder = this.parentFolders.find(f => f.id === folder.id);
        this.parentFolderId = parentFolder ? parentFolder.parentUserFolderId : null;
        const layerType = this.currentTabIndex === 1 ? 'layers' : 'drawing-layers';
        this.parentFolders = await this.userApi.queryUserFolders(this.userId, this.applicationId, layerType);
        this.parentFolders.push({ name: 'No parent', id: 0 });

        this.newFolderName.setValue(folder.name);
        this.showFolderEditor = true;
        this.folderEditing = true;
        this.cdr.detectChanges();
    }

    async newFolder() {
        const layerType = this.currentTabIndex === 1 ? 'layers' : 'drawing-layers';
        const folder: any = {
            name: this.newFolderName.value,
            category: layerType,
            parentUserFolderId: this.parentFolderId,
            userId: this.userId,
            applicationId: this.applicationId
        };

        if (this.currentFolderId) {
            folder.id = this.currentFolderId;
        }

        try {
            this.showFolderEditor = false;
            this.loading = { status: true, text: 'Creating new folder...' };
            await this.userApi.createNewUserFolder(folder);

            this.parentFolders = await this.userApi.queryUserFolders(this.userId, this.applicationId, layerType);
            this.parentFolders.push({ name: 'No parent', id: 0 });
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        } finally {
            //refresh folders and layers
            await this.updateLayerFolders();
            await this.refreshUserLayerCatalog();

            this.loading.status = false;
            this.cdr.detectChanges();
        }
    }

    async setTree(userLayersFolders, userLayers) {
        this.userLayerNodes = [];
        this.drawLayerNodes = [];
        const userLayerNodesTemp = [];
        this.loading = { status: true, text: 'Building Nodes...' };
        if (userLayersFolders) {
            userLayersFolders.sort((a, b) => a.name.localeCompare(b.name));
            userLayers.sort((a, b) => a.layer.name.localeCompare(b.layer.name));

            userLayerNodesTemp.push(...userLayersFolders.filter(f => !f.parentUserFolderId || f.parentUserFolderId === 0).map(f => this.buildTreeNode(f, userLayersFolders, userLayers)));
        }
        // Add orphan layers
        userLayerNodesTemp.push(...userLayers.map(l => {
            const layerObj = l.layer;
            layerObj.sharedWith = l.sharedWith || [];
            return {
                children: [],
                id: l.layer.id,
                layer: l.layer,
                name: l.layer.name,
                nodeType: 'leaf',
                owner: this.isOwner(l)
            };
        }));

        await this.updateNodesIsShared(userLayerNodesTemp);

        this.loading.status = false;
        if (this.currentTabIndex === 1) {
            this.userLayerNodes = userLayerNodesTemp;
        } else if (this.currentTabIndex === 2) {
            this.drawLayerNodes = userLayerNodesTemp;
        }

    }

    buildTreeNode(folder: any, userfolders: any[], userLayers: any[]) {
        const childFolders = userfolders.filter(f => f.parentUserFolderId === folder.id);
        const childLayers = this.getChildLayers(folder, userLayers);
        const groupNode = {
            children: [],
            id: folder.id,
            name: folder.name,
            nodeType: 'group'
        };
        groupNode.children.push(...childLayers.map(l => {
            l.layer.sharedWith = l.sharedWith;
            return {
                children: [],
                id: l.layer.id,
                layer: l.layer,
                name: l.layer.name,
                nodeType: 'leaf',
                owner: this.isOwner(l)
            };
        }));
        groupNode.children.push(...childFolders.map(cf => this.buildTreeNode(cf, userfolders, userLayers)));
        return groupNode;
    }

    getChildLayers(folder: any, userLayers: any[]) {
        const childLayers = [];
        folder.layers.forEach(fl => {
            const userLayer = userLayers.find(ul => ul.layer.id === fl.id);
            if (userLayer) {
                childLayers.push(userLayer);
                const idx = userLayers.indexOf(userLayer);
                userLayers.splice(idx, 1);

            }
        });
        childLayers.sort((a, b) => a.layer.name.localeCompare(b.layer.name));
        return childLayers;
    }

    isOwner(layerMetaData: any) {
        let retVal = true;
        if (layerMetaData.layer && layerMetaData.layer.userLayers) {
            const shareData = layerMetaData.layer.userLayers.find(ul => ul.userId === this.userId);
            if (shareData) {
                retVal = shareData.accessLevel === 'OWNER';
            }
        }
        return retVal;
    }

    async pickFolder(layer) {
        this.currentLayerId = layer.id;
        const parentFolder = this.userLayerFolders.find(f => f.layers.find(l => l.id === layer.id));
        if (parentFolder) {
            this.parentFolderId = parentFolder.id;
        }
        const layerType = this.currentTabIndex === 1 ? 'layers' : 'drawing-layers';
        this.parentFolders = await this.userApi.queryUserFolders(this.userId, this.applicationId, layerType);
        this.parentFolders.push({ name: 'No parent', id: 0 });
        this.showFolderPicker = true;
        this.cdr.detectChanges();
    }

    async changeFolder() {
        this.showFolderPicker = false;
        this.loading = { status: true, text: 'Changing folder...' };
        try {
            await this.layerService.saveLayerToUserFolder(this.currentLayerId, this.parentFolderId);
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        } finally {
            //refresh folders and layers
            await this.updateLayerFolders();
            await this.refreshUserLayerCatalog();

            this.loading.status = false;
            this.cdr.detectChanges();
        }
    }

    async deleteFolder(folder) {
        if (window.confirm('Are you sure you want to delete this folder and all layers it contains?')) {
            try {
                this.loading = { status: true, text: 'Deleting Folder...' };
                await this.deleteFolderAndLayers(folder);
            } catch (error) {
                this.LoggingService.Log(error, LogLevel.Error);
            } finally {
                this.parentFolders = this.parentFolders.filter(f => f.id !== folder.id);

                //refresh folders and layers
                await this.updateLayerFolders();
                await this.refreshUserLayerCatalog();

                this.loading.status = false;
                this.cdr.detectChanges();
            }
        }
    }

    async deleteFolderAndLayers(folder) {
        for (const child of folder.children) {
            if (child.nodeType === 'group') {
                await this.deleteFolderAndLayers(child);
            } else {
                await this.deleteLayer(child, false);
                if (child.id === this.mapViewRef.mapcoreViewProps.id) {
                    this.resetMap();
                }
            }

        }

        await this.userApi.deleteUserFolder(folder.id);
    }

    resetMap() {
        this.mapViewRef.mapcoreViewProps.id = null;
        if (this.mapViewRef.mapcoreViewProps.layers) {
            this.mapViewRef.mapcoreViewProps.layers.forEach(layer => {
                layer.id = null;
            });
        }
    }

    async deleteLayer(layer, askQuestion = true, multiple = false) {
        try {
            if (await this.getLayerSharedType(layer) !== ShareType.SharedWith) {
                if (askQuestion) {
                    if (window.confirm('Are you sure you want to delete layer?')) {
                        this.loading = { status: true, text: 'Deleting Folder...' };
                        await this.layerService.deletelayer(layer.id);
                    }
                } else {
                    this.loading = { status: true, text: 'Deleting Folder...' };
                    await this.layerService.deletelayer(layer.id);
                }
            } else {
                if (askQuestion) {
                    if (window.confirm('Are you sure you want to no longer have this layer shared with you?')) {
                        this.loading = { status: true, text: 'Removing Shared Layer Association...' };
                        /* remove shared layer association */
                        await this.layerApi.unshareLayer([this.userId], layer.id);
                    }
                } else {
                    this.loading = { status: true, text: 'Removing Shared Layer Association...' };
                    /* remove shared layer association */
                    await this.layerApi.unshareLayer([this.userId], layer.id);
                }
            }

            if (layer.id === this.mapViewRef.mapcoreViewProps.id) {
                this.resetMap();
            }
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        } finally {
            if (!multiple) {
                //refresh folders and layers
                await this.updateLayerFolders();
                await this.refreshUserLayerCatalog();
                this.loading.status = false;
                this.cdr.detectChanges();
            }
        }
    }

    shareLayer(layer) {
        this.openShareLayers.emit(layer.id);
    }

    unshareLayer(layer) {
        this.openUnshareLayers.emit(layer.id);
    }

    async updateLayerFolders() {
        if (this.userId) {
            const layerType = this.currentTabIndex === 1 ? 'layers' : 'drawing-layers';
            this.parentFolders = await this.userApi.queryUserFolders(this.userId, this.applicationId, layerType);
            this.parentFolders.push({ name: 'No parent', id: 0 });
            if (this.currentTabIndex === 1) {
                this.userLayerFolders = (await this.layerService.getUserLayersFolders(this.userId, this.applicationId))[0].folders;
            } else if (this.currentTabIndex === 2) {
                this.drawLayerFolders = (await this.layerService.getDrawLayersFolders(this.userId, this.applicationId))[0].folders;
            }
        }
    }

    /* Returns the number of users a given layer is shared with */
    async getLayerSharedType(layer: any): Promise<ShareType> {
        let retVal = ShareType.NotShared;
        const layers = await this.layerApi.getUserLayers(layer.id);
        if (layers.length > 0) {
            const userLayers = layers[0].userLayers as IUserLayer[];
            if (userLayers.length > 1) {
                const userLevel = userLayers.find(ul => ul.userId === this.userId);
                if (userLevel.accessLevel === 'OWNER') {
                    retVal = ShareType.SharedBy;
                } else {
                    retVal = ShareType.SharedWith;
                }
            }
        }
        return retVal;
    }

    async updateNodesIsShared(nodes: any[]) {
        await Promise.all(nodes.map(node => this.checkIfNodeIsShared(node)));
    }

    /* Iterate through nodes recursively, if the node is a layer, then set its isShared property.
     * The isShared property is used to display/hide unshare-layer button. */
    async checkIfNodeIsShared(node: any) {
        if (node.layer) {
            node.shareType = await this.getLayerSharedType(node.layer);
        }
        if (node.children) {
            for (const childNode of node.children) {
                this.checkIfNodeIsShared(childNode);
            }
        }
    }

    catalogTrack(item: ILayerCatalogItem) {
        return item.id;
    }
}

interface IUserLayer {
    accessLevel: 'F' | 'OWNER';
    userId: number;
}
